/**
 * DAT2 sniffing — enough to tell a real patch000.dat from a mis-drop.
 *
 * Fallout 2's archive format has no magic number at the *front*: byte 0 is just
 * the first byte of the first file's payload. The structure is anchored at the
 * end instead:
 *
 *   [ file data ... ][ directory tree ][ treeSize:u32le ][ totalSize:u32le ]
 *
 * `totalSize` is the length of the whole archive, so it doubles as a checksum
 * for "is this the file it thinks it is" — a truncated or half-downloaded .dat
 * fails it immediately. The tree begins `treeSize` bytes before those eight,
 * with a `u32le` file count followed by length-prefixed backslash-separated
 * names. Reading the first name is what separates a genuine DAT2 from eight
 * trailing bytes that happen to add up.
 *
 * We check all of it, because the alternative is a vault full of an mp3 the user
 * dropped by accident and an engine that fails to boot with no explanation.
 */

export class DatError extends Error {}

export interface Dat2Info {
  fileCount: number;
  treeSize: number;
  totalSize: number;
  /** First path in the tree, e.g. `Art\intrface\HELPSCRN.FRM`. Proof of life. */
  firstEntry: string;
}

/** Archive formats we recognise well enough to give a useful refusal. */
export type ArchiveKind = 'rar' | 'zip' | '7z' | 'gzip' | 'cab' | 'iso' | 'exe' | null;

const MAGIC: { kind: Exclude<ArchiveKind, null>; bytes: number[] }[] = [
  { kind: 'rar', bytes: [0x52, 0x61, 0x72, 0x21, 0x1a, 0x07] }, // "Rar!\x1a\x07"
  { kind: 'zip', bytes: [0x50, 0x4b, 0x03, 0x04] },
  { kind: 'zip', bytes: [0x50, 0x4b, 0x05, 0x06] }, // empty archive
  { kind: '7z', bytes: [0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c] },
  { kind: 'gzip', bytes: [0x1f, 0x8b] },
  { kind: 'cab', bytes: [0x4d, 0x53, 0x43, 0x46] }, // "MSCF"
  { kind: 'exe', bytes: [0x4d, 0x5a] }, // "MZ" — a self-extracting installer
];

/** Sniffs the leading bytes. Returns null when nothing recognisable is there. */
export async function sniffArchive(blob: Blob): Promise<ArchiveKind> {
  const head = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
  for (const m of MAGIC) {
    if (m.bytes.every((b, i) => head[i] === b)) return m.kind;
  }
  return null;
}

export function archiveLabel(kind: Exclude<ArchiveKind, null>): string {
  return { rar: '.rar', zip: '.zip', '7z': '.7z', gzip: 'gzip', cab: 'a Windows .cab', iso: 'a disc image', exe: 'a Windows executable' }[
    kind
  ];
}

const MIN_SIZE = 16;
/** The real patch000.dat is 2.2MB; master.dat is 333MB. Anything tiny is a mis-drop. */
const PLAUSIBLE_MIN = 1024;
const MAX_FILE_COUNT = 500_000;

export async function readDat2Info(blob: Blob): Promise<Dat2Info> {
  if (blob.size < MIN_SIZE || blob.size < PLAUSIBLE_MIN) {
    throw new DatError(`That file is only ${blob.size} bytes — far too small to be a Fallout 2 .dat.`);
  }

  const tail = new DataView(await blob.slice(blob.size - 8, blob.size).arrayBuffer());
  const treeSize = tail.getUint32(0, true);
  const totalSize = tail.getUint32(4, true);

  if (totalSize !== blob.size) {
    throw new DatError(
      `This is not a Fallout 2 DAT2 archive: its footer declares a length of ${totalSize} bytes but the file is ${blob.size}. ` +
        (totalSize > blob.size ? 'A partial download would look exactly like this.' : '')
    );
  }
  if (treeSize < 4 || treeSize > blob.size - 8) {
    throw new DatError('This is not a Fallout 2 DAT2 archive: its directory-tree size is out of range.');
  }

  const treeStart = blob.size - 8 - treeSize;
  // The count plus one length-prefixed name is all we need to prove structure.
  const headLen = Math.min(treeSize, 512);
  const head = new Uint8Array(await blob.slice(treeStart, treeStart + headLen).arrayBuffer());
  const hv = new DataView(head.buffer, head.byteOffset, head.byteLength);
  const fileCount = hv.getUint32(0, true);
  if (fileCount === 0 || fileCount > MAX_FILE_COUNT) {
    throw new DatError(`This is not a Fallout 2 DAT2 archive: it claims to hold ${fileCount} files.`);
  }
  if (head.length < 8) throw new DatError('This .dat is truncated: its directory tree ends immediately.');

  const nameLen = hv.getUint32(4, true);
  if (nameLen < 1 || nameLen > 255 || 8 + nameLen > head.length) {
    throw new DatError('This is not a Fallout 2 DAT2 archive: the first directory entry has an impossible name length.');
  }
  const nameBytes = head.subarray(8, 8 + nameLen);
  // Paths inside a DAT2 are plain ASCII with backslash separators. Anything
  // outside printable range means we are reading a coincidence, not a tree.
  for (const b of nameBytes) {
    if (b < 0x20 || b > 0x7e) {
      throw new DatError('This is not a Fallout 2 DAT2 archive: its directory tree is not readable text.');
    }
  }

  return { fileCount, treeSize, totalSize, firstEntry: String.fromCharCode(...nameBytes) };
}

/**
 * Full acceptance check for a dropped .dat, with archive detection first so the
 * user gets "we can't read .rar" rather than "not a DAT2 archive".
 */
export async function acceptDat(blob: Blob, filename: string): Promise<Dat2Info> {
  const kind = await sniffArchive(blob);
  if (kind === 'rar') {
    throw new DatError(
      `${filename} is a .rar archive, and we cannot read those — the browser has no built-in RAR decoder and this app ships no libraries. ` +
        'Extract it on your machine (macOS: The Unarchiver; Windows: 7-Zip or WinRAR) and drop the patch000.dat from inside it.'
    );
  }
  if (kind === '7z' || kind === 'cab' || kind === 'gzip') {
    throw new DatError(
      `${filename} is ${archiveLabel(kind)} archive, which we cannot read. Extract it locally and drop the patch000.dat from inside.`
    );
  }
  if (kind === 'exe') {
    throw new DatError(
      `${filename} is a Windows executable — probably the patch installer. Run it against a desktop copy of the game, or extract it ` +
        'with 7-Zip, and then drop the patch000.dat it produces.'
    );
  }
  if (kind === 'zip') {
    throw new DatError(`${filename} is a .zip; that is handled by the zip path, not here.`);
  }
  return readDat2Info(blob);
}
